import React, { useState, useEffect, useRef } from "react";
import { 
  Building2, 
  Upload, 
  Trash2, 
  Save,
  Image as ImageIcon,
  RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * CompanySettingsCard - Manage company logo and branding for PDF quotes
 */
const CompanySettingsCard = () => {
  const [settings, setSettings] = useState({
    company_name: '',
    ruc: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    logo_data: null,
    payment_terms: '',
    footer_text: '',
    bank_accounts: []
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const fileInputRef = useRef(null);

  // Load settings on mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/company-settings`);
      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/company-settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        toast.success('Configuración guardada exitosamente');
      } else {
        toast.error('Error al guardar configuración');
      }
    } catch (error) {
      // console.error('Error saving settings:', error);
      toast.error('Error al guardar configuración');
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Solo se aceptan archivos de imagen');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('El archivo es muy grande (máx 5MB)');
      return;
    }

    setIsUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/company-settings/logo`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({ ...prev, logo_data: data.logo_data }));
        toast.success('Logo subido exitosamente');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al subir logo');
      }
    } catch (error) {
      // console.error('Error uploading logo:', error);
      toast.error('Error al subir logo');
    } finally {
      setIsUploadingLogo(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteLogo = async () => {
    try {
      const response = await fetch(`${API_URL}/api/company-settings/logo`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setSettings(prev => ({ ...prev, logo_data: null }));
        toast.success('Logo eliminado. Se usará el logo predeterminado.');
      } else {
        toast.error('Error al eliminar logo');
      }
    } catch (error) {
      // console.error('Error deleting logo:', error);
      toast.error('Error al eliminar logo');
    }
  };

  const handleLoadDefaultLogo = async () => {
    try {
      const response = await fetch(`${API_URL}/api/company-settings/default-logo`);
      if (response.ok) {
        const data = await response.json();
        if (data.logo_data) {
          setSettings(prev => ({ ...prev, logo_data: data.logo_data }));
          toast.success('Logo predeterminado cargado');
        }
      }
    } catch (error) {
      console.error('Error loading default logo:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (isLoading) {
    return (
      <Card className="metric-card">
        <CardContent className="p-8 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="metric-card" data-testid="company-settings-card">
      <CardHeader className="border-b border-slate-100 p-4">
        <CardTitle className="text-lg font-medium text-slate-900 flex items-center gap-2">
          <Building2 className="w-5 h-5" />
          Configuración de Empresa
        </CardTitle>
        <CardDescription className="text-slate-500">
          Configura el logo y datos de la empresa para las cotizaciones PDF
        </CardDescription>
      </CardHeader>
      
      <CardContent className="p-4 space-y-6">
        {/* Logo Section */}
        <div className="space-y-4">
          <Label className="text-sm font-semibold text-slate-700">Logo de Empresa</Label>
          
          <div className="flex items-start gap-6">
            {/* Logo Preview */}
            <div className="flex-shrink-0">
              <div 
                className="w-40 h-28 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center bg-slate-50 overflow-hidden"
                data-testid="logo-preview"
              >
                {settings.logo_data ? (
                  <img 
                    src={settings.logo_data} 
                    alt="Logo de empresa" 
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <div className="text-center text-slate-400">
                    <ImageIcon className="w-8 h-8 mx-auto mb-1" />
                    <span className="text-xs">Sin logo</span>
                  </div>
                )}
              </div>
            </div>

            {/* Logo Actions */}
            <div className="flex-1 space-y-3">
              <p className="text-sm text-slate-600">
                Sube el logo de tu empresa para que aparezca en las cotizaciones PDF.
                El logo predeterminado de MOBILIARS se usará si no subes uno.
              </p>
              
              <div className="flex flex-wrap gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                  data-testid="logo-file-input"
                />
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingLogo}
                  data-testid="upload-logo-btn"
                >
                  {isUploadingLogo ? (
                    <>
                      <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-2" />
                      Subiendo...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Subir Logo
                    </>
                  )}
                </Button>

                {settings.logo_data && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDeleteLogo}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid="delete-logo-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Eliminar
                  </Button>
                )}

                {!settings.logo_data && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleLoadDefaultLogo}
                    data-testid="load-default-logo-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Cargar Logo MOBILIARS
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <hr className="border-slate-200" />

        {/* Company Info Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="company_name">Nombre de Empresa</Label>
            <Input
              id="company_name"
              value={settings.company_name}
              onChange={(e) => handleInputChange('company_name', e.target.value)}
              placeholder="MOBILIARS S.A.C."
              data-testid="company-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="ruc">RUC</Label>
            <Input
              id="ruc"
              value={settings.ruc}
              onChange={(e) => handleInputChange('ruc', e.target.value)}
              placeholder="20610891891"
              data-testid="company-ruc-input"
            />
          </div>

          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="address">Dirección</Label>
            <Input
              id="address"
              value={settings.address}
              onChange={(e) => handleInputChange('address', e.target.value)}
              placeholder="Calle Los Talladores 190, Ate - Lima"
              data-testid="company-address-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Teléfono</Label>
            <Input
              id="phone"
              value={settings.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              placeholder="(01) 349-1414"
              data-testid="company-phone-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={settings.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="ventas@mobiliars.pe"
              data-testid="company-email-input"
            />
          </div>
        </div>

        {/* Payment Terms */}
        <div className="space-y-2">
          <Label htmlFor="payment_terms">Condiciones de Pago</Label>
          <Textarea
            id="payment_terms"
            value={settings.payment_terms}
            onChange={(e) => handleInputChange('payment_terms', e.target.value)}
            placeholder="50% adelanto para iniciar fabricación. 50% restante contra entrega de mercadería."
            className="min-h-[80px]"
            data-testid="payment-terms-input"
          />
        </div>

        {/* Footer Text */}
        <div className="space-y-2">
          <Label htmlFor="footer_text">Texto de Pie de Página</Label>
          <Input
            id="footer_text"
            value={settings.footer_text}
            onChange={(e) => handleInputChange('footer_text', e.target.value)}
            placeholder="Cotización válida por 15 días. Precios sujetos a variación según tipo de cambio."
            data-testid="footer-text-input"
          />
        </div>

        {/* Save Button */}
        <div className="flex justify-end pt-4 border-t border-slate-100">
          <Button
            onClick={handleSaveSettings}
            disabled={isSaving}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="save-settings-btn"
          >
            {isSaving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Guardando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Guardar Configuración
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default CompanySettingsCard;
